import { useState, useMemo } from 'react';
import { X, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  extraLargeTextSize,
  semiBoldText,
  smallTextSize,
  secondaryText,
  primaryText,
  mediumText,
  borderPrimary,
  whiteBG,
} from '../../../../styling/stylingHelper';
import { useFuzzySearch } from '../../../../hooks/useFuzzySearch';
import { CostPriceAnalysis } from '../../../../services/costPrice/costPriceService';

interface EditRecipeDishLinkModalProps {
  currentCostPriceId: string | null;
  analyses: CostPriceAnalysis[];
  onSave: (costPriceId: string | null) => Promise<void>;
  onClose: () => void;
}

// Picks the cost price item a recipe is linked to. Unlike the old revenue-item
// picker this also offers CUSTOM items (no POS sales item), which is the whole
// point of the link — a custom dish can now carry a category onto the recipe.
const EditRecipeDishLinkModal = ({
  currentCostPriceId,
  analyses,
  onSave,
  onClose,
}: EditRecipeDishLinkModalProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(currentCostPriceId);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // CHOICE analyses are BYO add-on choices, not dishes — never link targets.
  const linkableAnalyses = useMemo(
    () => analyses.filter((a) => a.costPriceType !== 'CHOICE'),
    [analyses]
  );

  const filteredAnalyses = useFuzzySearch({
    data: linkableAnalyses,
    keys: ['costPriceName', 'menuItemName'],
    query: search,
    threshold: 0.5,
  });

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await onSave(selectedId);
      onClose();
    } catch (err) {
      console.error('Failed to save cost price link:', err);
      setError(t('recipes.saveLinkFailed'));
      setIsLoading(false);
    }
  };

  const hasChanged = selectedId !== currentCostPriceId;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/60 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-card dark:bg-card-dark rounded-none sm:rounded-lg shadow-xl max-w-2xl w-full h-full sm:h-auto max-h-none sm:max-h-[80vh] border border-zinc-200 dark:border-white/20 flex flex-col">
        {/* Header - Fixed */}
        <div className="p-6 border-b border-border dark:border-border-dark flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className={cn(extraLargeTextSize, semiBoldText, primaryText)}>
              {t('recipes.linkedDish')}
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-muted dark:text-muted-dark hover:text-text dark:hover:text-text-dark rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className={cn(smallTextSize, secondaryText)}>
            {t('recipes.linkedDishDescription')}
          </p>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted dark:text-muted-dark" />
              <input
                type="text"
                placeholder={t('recipes.searchDishes')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border dark:border-border-dark rounded-lg bg-background dark:bg-background-dark text-text dark:text-text-dark placeholder-muted dark:placeholder-muted-dark focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-dark"
              />
            </div>
          </div>

          {/* No Link Option */}
          <div
            onClick={() => setSelectedId(null)}
            className={cn(
              'mb-3 p-3 border rounded-lg cursor-pointer transition-colors',
              selectedId === null
                ? 'border-blue-500 dark:border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                : 'border-border dark:border-border-dark hover:bg-muted/5 dark:hover:bg-muted-dark/5'
            )}
          >
            <div className={cn(mediumText, primaryText)}>{t('recipes.noLink')}</div>
            <div className={cn(smallTextSize, secondaryText)}>{t('recipes.noLinkDescription')}</div>
          </div>

          {/* Cost price items */}
          <div className="space-y-2">
            {filteredAnalyses.length === 0 ? (
              <div className={cn('p-4 text-center', secondaryText)}>
                {search ? t('recipes.noDishesMatchSearch') : t('recipes.noDishesAvailable')}
              </div>
            ) : (
              filteredAnalyses.map((analysis) => {
                const isSelected = selectedId === analysis.id;
                // An analysis with a revenue item is POS-backed; without one it
                // is a custom dish that has no sales data of its own.
                const isPos = !!analysis.menuItemName;

                return (
                  <div
                    key={analysis.id}
                    onClick={() => setSelectedId(analysis.id)}
                    className={cn(
                      'p-3 border rounded-lg cursor-pointer transition-colors',
                      isSelected
                        ? 'border-blue-500 dark:border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                        : 'border-border dark:border-border-dark hover:bg-muted/5 dark:hover:bg-muted-dark/5'
                    )}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={cn(mediumText, primaryText)}>{analysis.costPriceName}</span>
                      <span
                        className={cn(
                          'text-xs px-2 py-0.5 rounded-md font-medium',
                          isPos
                            ? 'bg-[#ECFDF3] text-[#027A48] dark:bg-green-900/20 dark:text-green-400'
                            : 'bg-[#F2F4F7] text-[#475467] dark:bg-zinc-800 dark:text-zinc-400'
                        )}
                      >
                        {isPos ? t('recipes.posItem') : t('recipes.customItem')}
                      </span>
                    </div>
                    {/* Keep showing the derived revenue item so the user sees
                        which sales data this link ties the recipe to. */}
                    {isPos && (
                      <div className={cn(smallTextSize, secondaryText, 'mt-0.5')}>
                        {t('recipes.salesItem', { name: analysis.menuItemName })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0 border-t border-borderPrimary dark:border-borderPrimary-dark">
          <div className="p-6">
            {error && (
              <p className={cn(smallTextSize, mediumText, 'mb-3 text-red-600 dark:text-red-400')}>
                {error}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className={cn(
                  smallTextSize,
                  secondaryText,
                  semiBoldText,
                  'rounded-xl px-4 py-3',
                  whiteBG,
                  'border',
                  borderPrimary,
                  'hover:bg-gray-50 dark:hover:bg-white/5',
                  'disabled:opacity-50'
                )}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanged || isLoading}
                className={cn(
                  smallTextSize,
                  'text-white dark:text-white',
                  semiBoldText,
                  'bg-darkGreenColor dark:bg-darkGreenColor-dark rounded-xl px-4 py-3',
                  'hover:bg-darkGreenColor/95',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isLoading ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRecipeDishLinkModal;
